import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights } from 'nallian-shared-ui/lib/lib/routesHelper';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { formatDateTime, formatRelativeTime } from 'nallian-shared-ui/lib/lib/dateTime';
import {
  PageContent,
  PageTitle,
  Tab,
  Tb,
  Link,
  Grid,
  Table,
  Loader,
  Header,
  Menu,
} from 'nallian-shared-ui/lib/components';
import findKey from 'lodash/findKey';
import { history } from 'nallian-shared-ui/lib/config/history';
import communityService from '../services/communityService';
import CommunityMembersOverview from '../components/communityMembers/CommunityMembersOverview';
import CommunityRolesOverview from '../components/communityRoles/CommunityRolesOverview';
import JoinRequestsOverview from '../components/joinRequests/JoinRequestsOverview';
import CommunityAppsOverview from '../components/communityApps/CommunityAppsOverview';
import RelationsOverview from '../components/relations/RelationsOverview';
import OnboardingTemplates from '../components/onboardingTemplates/OnboardingTemplatesOverview';
import EditCommunity from '../components/details/EditCommunity';
import NotificationSettings from '../components/notificationSettings/NotificationSettings';
import Hosts from '../components/hosts/Hosts';
import LegalAndTexts from '../components/legalAndTexts/LegalAndTexts';
import Blobs from '../components/blobs/Blobs';
import IdentificationCodeTypesOverview from '../components/identificationCodeTypes/IdentificationCodeTypes';

import { COMMUNITY_DETAIL_TABS, COMMUNITY_TAB_ACTIONS } from '../constants';

export const COMMUNITY_MEMBERS_PAGESIZE = 50;

const TABS_INDEX_MAPPING = {
  [COMMUNITY_DETAIL_TABS.DETAILS]: 0,
  [COMMUNITY_DETAIL_TABS.MEMBERS]: 1,
  [COMMUNITY_DETAIL_TABS.ROLES]: 2,
  [COMMUNITY_DETAIL_TABS.JOIN_REQUESTS]: 3,
  [COMMUNITY_DETAIL_TABS.APPS]: 4,
  [COMMUNITY_DETAIL_TABS.RELATIONS]: 5,
  [COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES]: 6,
  [COMMUNITY_DETAIL_TABS.IDENTIFICATION_CODE_TYPES]: 7,
  [COMMUNITY_DETAIL_TABS.NOTIFICATION_SETTINGS]: 8,
  [COMMUNITY_DETAIL_TABS.HOST_URLS]: 9,
  [COMMUNITY_DETAIL_TABS.LEGAL_AND_TEXTS]: 10,
  [COMMUNITY_DETAIL_TABS.BLOBS]: 11,
};

const COMMUNITY_ROLES_RIGHTS = ['COMMUNITYROLES.MANAGE.ALL', 'COMMUNITYROLES.READ.ALL'];
const JOIN_REQUESTS_RIGHTS = ['JOINREQUESTS.MANAGE.ALL', 'JOINREQUESTS.READ.ALL'];
const COMMUNITY_MEMBER_RIGHTS = ['COMMUNITYMEMBERS.MANAGE.ALL', 'COMMUNITYMEMBERS.READ.ALL'];
const ONBOARDING_TEMPLATES_RIGHTS = ['onboarding-templates/read'];
const COMMUNITY_APPS_RIGHTS = ['COMMUNITYAPPS.MANAGE.ALL', 'COMMUNITYAPPS.READ.ALL'];
const COMMUNITY_MANAGE_RIGHTS = ['COMMUNITIES.MANAGE.ALL'];
const COMMUNITY_NOTIFICATION_RIGHTS = ['COMMUNITIES.MANAGE.ALL'];
const COMMUNITY_HOSTS_RIGHTS = ['COMMUNITIES.MANAGE.ALL'];
const COMMUNITY_LEGAL_RIGHTS = ['COMMUNITIES.MANAGE.ALL'];
const COMMUNITY_BLOBS_RIGHTS = [
  'community-blobs/read',
  'community-blobs/create',
  'community-blobs/update',
];

const CommunityDetail = ({ communityFetch, dispatchCommunityGet }) => {
  const { id, tab, action, actionId } = useParams();
  const { t } = useTranslation();

  const rights = useSelector(GetRights);
  const community = communityFetch.value;

  useEffect(() => {
    if (id) dispatchCommunityGet(id);
  }, [dispatchCommunityGet, id]);

  const panes = [
    hasSomeRights(rights, COMMUNITY_MEMBER_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.DETAILS,
          menuItem: 'Details',
          render: () => (
            <Tb.Pane>
              <Loader active={communityFetch.pending} inline="centered" />
              {communityFetch.fulfilled && (
                <Grid columns={2} stackable>
                  <Grid.Column>
                    <Header>{t('general')}</Header>
                    <Table>
                      <Table.Row>
                        <Table.HeaderCell width={4}>{t('name')}</Table.HeaderCell>
                        <Table.Cell>{community.name}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('tag')}</Table.HeaderCell>
                        <Table.Cell>{community.tag}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('description')}</Table.HeaderCell>
                        <Table.Cell>{community.description || '-'}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('url')}</Table.HeaderCell>
                        <Table.Cell>{community.url || '-'}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('language')}</Table.HeaderCell>
                        <Table.Cell>{community.defaultLanguageId?.toUpperCase() || '-'}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('themeName')}</Table.HeaderCell>
                        <Table.Cell>{community.themeName || '-'}</Table.Cell>
                      </Table.Row>
                    </Table>
                    <Header>{t('metaData')}</Header>
                    <Table>
                      <Table.Row>
                        <Table.HeaderCell width={4}>{t('createdOn')}</Table.HeaderCell>
                        <Table.Cell>
                          {`${formatDateTime(community.createdOn)}`}
                          &nbsp;-&nbsp;
                          {formatRelativeTime(community.createdOn)}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell>{t('lastModifiedOn')}</Table.HeaderCell>
                        <Table.Cell>
                          {`${formatDateTime(community.modifiedOn)}`}
                          &nbsp;-&nbsp;
                          {formatRelativeTime(community.modifiedOn)}
                        </Table.Cell>
                      </Table.Row>
                    </Table>
                  </Grid.Column>
                  <Grid.Column>
                    <Header>{t('tenantInformation')}</Header>
                    <Table>
                      <Table.Row>
                        <Table.HeaderCell width={4}>{t('tenantHostUrl')}</Table.HeaderCell>
                        <Table.Cell>{community.tenantHostUrl}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.HeaderCell width={4}>{t('tenantId')}</Table.HeaderCell>
                        <Table.Cell>{community.tenantId}</Table.Cell>
                      </Table.Row>
                    </Table>
                    <Header>{t('location')}</Header>
                    <Table>
                      <Table.Row>
                        <Table.HeaderCell width={4}>{t('timezone')}</Table.HeaderCell>
                        <Table.Cell>{community.timeZone ? community.timeZone : '-'}</Table.Cell>
                      </Table.Row>
                    </Table>
                  </Grid.Column>
                </Grid>
              )}
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_MEMBER_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.MEMBERS,
          menuItem: 'Members',
          render: () => (
            <Tb.Pane>
              <CommunityMembersOverview
                action={action}
                actionId={actionId}
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_ROLES_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.ROLES,
          menuItem: 'Roles',
          render: () => (
            <Tb.Pane>
              <CommunityRolesOverview
                action={action}
                actionId={actionId}
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, JOIN_REQUESTS_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.JOIN_REQUESTS,
          menuItem: 'Join Requests',
          render: () => (
            <Tb.Pane>
              <JoinRequestsOverview
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.JOIN_REQUESTS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_APPS_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.APPS,
          menuItem: 'Apps',
          render: () => (
            <Tb.Pane>
              <CommunityAppsOverview
                action={action}
                actionId={actionId}
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_APPS_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.RELATIONS,
          menuItem: 'Relations',
          render: () => (
            <Tb.Pane>
              <RelationsOverview
                action={action}
                actionId={actionId}
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.RELATIONS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, ONBOARDING_TEMPLATES_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES,
          menuItem: 'Onboarding Templates',
          render: () => (
            <Tb.Pane>
              <OnboardingTemplates
                action={action}
                actionId={actionId}
                communityId={id}
                communityName={communityFetch.value?.name || '...'}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    {
      key: COMMUNITY_DETAIL_TABS.IDENTIFICATION_CODE_TYPES,
      menuItem: 'Identification code types',
      render: () => (
        <Tb.Pane>
          <IdentificationCodeTypesOverview
            action={action}
            actionId={actionId}
            communityId={id}
            communityName={communityFetch.value?.name || '...'}
            rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.IDENTIFICATION_CODE_TYPES}`}
          />
        </Tb.Pane>
      ),
    },
    hasSomeRights(rights, COMMUNITY_NOTIFICATION_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.NOTIFICATION_SETTINGS,
          menuItem: 'Notification',
          render: () => (
            <Tb.Pane>
              <NotificationSettings
                communityId={id}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.NOTIFICATION_SETTINGS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_HOSTS_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.HOST_URLS,
          menuItem: 'Hosts',
          render: () => (
            <Tb.Pane>
              <Hosts
                communityId={id}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.HOST_URLS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_LEGAL_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.LEGAL_AND_TEXTS,
          menuItem: 'Texts & legal',
          render: () => (
            <Tb.Pane>
              <LegalAndTexts
                communityId={id}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.HOST_URLS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    hasSomeRights(rights, COMMUNITY_BLOBS_RIGHTS)
      ? {
          key: COMMUNITY_DETAIL_TABS.BLOBS,
          menuItem: 'Blobs',
          render: () => (
            <Tb.Pane>
              <Blobs
                communityId={id}
                rootUrl={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.BLOBS}`}
              />
            </Tb.Pane>
          ),
        }
      : undefined,
    {
      menuItem: (
        <Menu.Menu position="right" key="menu">
          <HasSomeRights rights={COMMUNITY_MANAGE_RIGHTS}>
            {tab === COMMUNITY_DETAIL_TABS.DETAILS && id ? (
              <Menu.Item
                icon="pencil"
                content="Edit"
                onClick={() =>
                  history.push(
                    `/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.DETAILS}/${COMMUNITY_TAB_ACTIONS.EDIT_COMMUNITY}/${id}`
                  )
                }
              />
            ) : (
              <div />
            )}
          </HasSomeRights>
        </Menu.Menu>
      ),
    },
  ].filter(p => p);

  return (
    <>
      <PageTitle flexed>
        <Link to="/communities">Communities</Link>
        &nbsp;/&nbsp;{communityFetch.value?.name || '...'}
      </PageTitle>
      <PageContent>
        <EditCommunity open={actionId && action === COMMUNITY_TAB_ACTIONS.EDIT_COMMUNITY} />
        <Tab
          panes={panes}
          activeIndex={TABS_INDEX_MAPPING[tab || COMMUNITY_DETAIL_TABS.ROLES]}
          onTabChange={(e, { activeIndex }) =>
            history.push(
              `/communities/edit/${id}/${findKey(
                TABS_INDEX_MAPPING,
                index => index === activeIndex
              )}`
            )
          }
        />
      </PageContent>
    </>
  );
};

CommunityDetail.propTypes = {
  dispatchCommunityGet: PropTypes.func.isRequired,
  communityFetch: PropTypes.object.isRequired,
};

export default communityService(CommunityDetail);
